import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AuthService } from '../../services/auth.service';

type SecurityTab = 'overview' | 'recovery_codes' | 'reconfigure';

@Component({
  selector: 'app-security-modal',
  standalone: false,
  templateUrl: './security-modal.component.html',
  styleUrl: './security-modal.component.css',
})
export class SecurityModalComponent implements OnInit {
  @Input() show = false;
  @Output() close = new EventEmitter<void>();

  activeTab: SecurityTab = 'overview';
  loading = false;
  errorMessage = '';
  successMessage = '';

  // Recovery codes
  recoveryCodes: string[] = [];
  copiedRecoveryCodes = false;

  // Reconfiguration
  qrCodeDataUrl = '';
  secret = '';
  newRecoveryCodes: string[] = [];
  totpCode = '';
  copiedSecret = false;
  hasSavedNewRecoveryCodes = false;

  constructor(public authService: AuthService) {}

  ngOnInit() {
    // Initial state
  }

  onClose() {
    this.resetState();
    this.close.emit();
  }

  resetState() {
    this.activeTab = 'overview';
    this.errorMessage = '';
    this.successMessage = '';
    this.totpCode = '';
    this.secret = '';
    this.qrCodeDataUrl = '';
    this.recoveryCodes = [];
    this.newRecoveryCodes = [];
    this.hasSavedNewRecoveryCodes = false;
  }

  setTab(tab: SecurityTab) {
    this.activeTab = tab;
    this.errorMessage = '';
    this.successMessage = '';

    if (tab === 'recovery_codes' && this.recoveryCodes.length === 0) {
      this.fetchRecoveryCodes();
    } else if (tab === 'reconfigure') {
      this.initReconfigure();
    }
  }

  async fetchRecoveryCodes() {
    this.loading = true;
    this.errorMessage = '';
    try {
      const res = await this.authService.getRecoveryCodes();
      this.recoveryCodes = res.recovery_codes || [];
    } catch (err: any) {
      console.error('Failed to get recovery codes:', err);
      this.errorMessage =
        err?.error_message ||
        err?.message ||
        'Could not load recovery codes. You can rotate and generate new ones below.';
    } finally {
      this.loading = false;
    }
  }

  async rotateRecoveryCodes() {
    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';
    try {
      const res = await this.authService.rotateRecoveryCodes();
      this.recoveryCodes = res.recovery_codes || [];
      this.successMessage = 'Successfully generated new recovery codes! Old codes are now invalid.';
    } catch (err: any) {
      console.error('Failed to rotate recovery codes:', err);
      this.errorMessage =
        err?.error_message ||
        err?.message ||
        'Failed to generate new recovery codes. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  copyRecoveryCodes(codes: string[]) {
    if (!codes.length) return;
    navigator.clipboard.writeText(codes.join('\n')).then(() => {
      this.copiedRecoveryCodes = true;
      setTimeout(() => (this.copiedRecoveryCodes = false), 2000);
    });
  }

  downloadRecoveryCodes(codes: string[]) {
    if (!codes.length) return;
    const memberEmail = this.authService.getMember()?.getSync()?.email_address || 'Moneco Member';
    const content = [
      '=====================================================',
      'MONECO DASHBOARD - EMERGENCY MFA RECOVERY CODES',
      '=====================================================',
      `Generated: ${new Date().toISOString()}`,
      `Account: ${memberEmail}`,
      '',
      'IMPORTANT SECURITY WARNING:',
      '- Store these codes in a secure, confidential place (e.g. password manager).',
      '- Each recovery code can only be used ONCE to sign in if you lose your phone.',
      '- Do NOT share these codes with anyone.',
      '',
      'RECOVERY CODES:',
      ...codes.map((code, idx) => `  ${idx + 1}. ${code}`),
      '=====================================================',
    ].join('\n');

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `moneco-dashboard-recovery-codes-${new Date().toISOString().slice(0, 10)}.txt`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  async initReconfigure() {
    this.loading = true;
    this.errorMessage = '';
    this.totpCode = '';
    try {
      const res = await this.authService.createTOTP();
      if (res.qr_code) {
        if (res.qr_code.startsWith('data:')) {
          this.qrCodeDataUrl = res.qr_code;
        } else if (res.qr_code.startsWith('<svg') || res.qr_code.includes('<svg')) {
          this.qrCodeDataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(res.qr_code)}`;
        } else {
          this.qrCodeDataUrl = `data:image/svg+xml;base64,${res.qr_code}`;
        }
      }
      this.secret = res.secret || '';
      this.newRecoveryCodes = res.recovery_codes || [];
    } catch (err: any) {
      console.error('Failed to create new TOTP registration:', err);
      this.errorMessage =
        err?.error_message ||
        err?.message ||
        'Failed to start 2FA re-configuration. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  copySecret() {
    if (!this.secret) return;
    navigator.clipboard.writeText(this.secret).then(() => {
      this.copiedSecret = true;
      setTimeout(() => (this.copiedSecret = false), 2000);
    });
  }

  async confirmReconfigure() {
    this.errorMessage = '';
    const cleanCode = this.totpCode.trim();

    if (!cleanCode || !/^\d{6}$/.test(cleanCode)) {
      this.errorMessage = 'Please enter a valid 6-digit confirmation code.';
      return;
    }

    this.loading = true;
    try {
      await this.authService.authenticateTOTP(cleanCode, {
        enroll: true,
        setDefaultMfa: true,
      });
      this.successMessage = 'Authenticator app successfully updated!';
      this.activeTab = 'overview';
    } catch (err: any) {
      console.error('Failed to confirm TOTP reconfigure:', err);
      this.errorMessage =
        err?.error_message ||
        err?.message ||
        'Invalid verification code. Please check your authenticator app and try again.';
    } finally {
      this.loading = false;
    }
  }
}
